import * as Sentry from '@sentry/react-native';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isTransientNetworkError, toError } from '../utils/errorUtils';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';
import { useAuth } from './AuthContext';
import { useTier } from './TierContext';

/**
 * NegotiationContext - Global negotiation state for badge counts and active limit.
 *
 * Provides:
 * - activeCount, unreadCount, pendingDraftCount
 * - activeLimit (mirrors cap_config seeded by Step 6:
 *     active_negotiations | Pro   | active | 5
 *     active_negotiations | Trial | active | 1)
 * - canStartNew (activeCount < activeLimit)
 * - Realtime subscription on negotiations table
 *
 * Cap selection rule (Step 6 cap_config keys on `tier`, not `subscription_status`):
 *   Paid Pro entitlements (subscription_status in {active, grandfather_active,
 *   cancelled-with-grace, paused-with-grace, past_due-with-grace}) → PRO cap.
 *   trial_active → TRIAL cap (1). Server rejects beyond that — keep FE in sync
 *   so users do not click into a silent 403.
 *   Anything else: defensive 1-cap (matches Trial).
 */

const PRO_ACTIVE_LIMIT = 5;
const TRIAL_ACTIVE_LIMIT = 1;

export const NegotiationContext = createContext({
  activeCount: 0,
  unreadCount: 0,
  pendingDraftCount: 0,
  negotiations: [],
  loading: true,
  activeLimit: TRIAL_ACTIVE_LIMIT,
  canStartNew: true,
  refetch: async () => {},
});

export const NegotiationProvider = ({ children }) => {
  const { user } = useAuth();
  const { isPro, subscriptionStatus } = useTier();
  // Trial users have isPro=true (PRO_ACCESS_STATUSES includes trial_active) but
  // the server-side cap is 1, not 5. Differentiate explicitly here so the FE
  // doesn't let trial users click into a guaranteed 403.
  const activeLimit = isPro && subscriptionStatus !== 'trial_active'
    ? PRO_ACTIVE_LIMIT
    : TRIAL_ACTIVE_LIMIT;
  const [state, setState] = useState({
    activeCount: 0,
    unreadCount: 0,
    pendingDraftCount: 0,
    negotiations: [],
    loading: true,
  });
  const channelRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchCounts = useCallback(async () => {
    if (!user) {
      setState({
        activeCount: 0,
        unreadCount: 0,
        pendingDraftCount: 0,
        negotiations: [],
        loading: false,
      });
      return;
    }

    try {
      logger.log('[NegotiationContext] Fetching negotiation counts for user:', user.id);

      const { data: negotiations, error } = await supabase
        .from('negotiations')
        .select('id, status, autonomy, updated_at')
        .eq('user_id', user.id)
        .in('status', ['setup', 'active', 'stalled', 'deal_reached', 'handed_off']);

      if (error) {
        logger.error('[NegotiationContext] Failed to fetch negotiations:', error);
        throw error;
      }

      const activeCount = (negotiations || []).filter(
        (n) => n.status === 'setup' || n.status === 'active'
      ).length;

      // Count unread: negotiations that have unread dealer messages
      let unreadCount = 0;
      let pendingDraftCount = 0;

      if (negotiations && negotiations.length > 0) {
        const activeIds = negotiations
          .filter((n) => n.status === 'active' || n.status === 'setup')
          .map((n) => n.id);

        if (activeIds.length > 0) {
          // Count negotiations with unread dealer messages
          const { count: unread } = await supabase
            .from('negotiation_messages')
            .select('negotiation_id', { count: 'exact', head: true })
            .in('negotiation_id', activeIds)
            .eq('role', 'dealer_received')
            .eq('read', false);

          unreadCount = unread || 0;

          // Count negotiations with pending drafts. Two collapses apply:
          //
          //   1. Regenerations create multiple status='draft' rows per
          //      (negotiation_id, round_number). VersionBrowser keeps the
          //      siblings around so the user can switch between V1/V2/V3,
          //      so we can't mark them discarded; we dedupe here instead.
          //   2. Once any draft in a round has been acted on (approved,
          //      auto_approved, retry_pending, or already promoted to
          //      role='agent_sent'/'sent' by the worker), the leftover
          //      'draft' regen rows in that same round are stale and must
          //      not count. The round is closed.
          //
          // We fetch one wider slice and compute both in JS.
          const { data: draftRows, error: draftErr } = await supabase
            .from('negotiation_messages')
            .select('negotiation_id, round_number, role, status')
            .in('negotiation_id', activeIds)
            .in('role', ['agent_draft', 'agent_sent', 'user_written']);

          if (draftErr) throw draftErr;

          const resolvedRoundsByNeg = new Map();
          const pendingByNeg = new Map();
          const RESOLVED_DRAFT_STATUSES = new Set([
            'approved',
            'auto_approved',
            'retry_pending',
            'sent',
          ]);

          for (const row of draftRows || []) {
            if (row.round_number == null) continue;
            const isResolved =
              row.role === 'agent_sent' ||
              (row.role === 'user_written' &&
                (row.status === 'sent' || row.status === 'approved')) ||
              (row.role === 'agent_draft' &&
                RESOLVED_DRAFT_STATUSES.has(row.status));
            if (isResolved) {
              if (!resolvedRoundsByNeg.has(row.negotiation_id)) {
                resolvedRoundsByNeg.set(row.negotiation_id, new Set());
              }
              resolvedRoundsByNeg.get(row.negotiation_id).add(row.round_number);
              continue;
            }
            if (row.role === 'agent_draft' && row.status === 'draft') {
              if (!pendingByNeg.has(row.negotiation_id)) {
                pendingByNeg.set(row.negotiation_id, new Set());
              }
              pendingByNeg.get(row.negotiation_id).add(row.round_number);
            }
          }

          let pending = 0;
          for (const [negId, rounds] of pendingByNeg) {
            const resolved = resolvedRoundsByNeg.get(negId);
            const liveRounds = resolved
              ? [...rounds].filter((r) => !resolved.has(r))
              : [...rounds];
            if (liveRounds.length > 0) pending += 1;
          }
          pendingDraftCount = pending;
        }
      }

      setState({
        activeCount,
        unreadCount,
        pendingDraftCount,
        negotiations: negotiations || [],
        loading: false,
      });

      logger.log('[NegotiationContext] Counts updated:', { activeCount, unreadCount, pendingDraftCount });
    } catch (error) {
      logger.error('[NegotiationContext] Error fetching counts:', error);
      // Transient network blips leave badge counts stale, not broken — don't
      // capture them. Wrap genuine errors so Sentry can group them.
      if (!isTransientNetworkError(error)) {
        Sentry.captureException(toError(error), {
          tags: { component: 'NegotiationContext' },
          extra: { userId: user?.id },
        });
      }
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [user]);

  useEffect(() => {
    fetchCounts();

    if (!user) return;

    // Subscribe to Realtime on negotiations table
    const channelName = `negotiation-counts:${user.id}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiations',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Debounce refetch
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            fetchCounts();
          }, 500);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiations',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            fetchCounts();
          }, 500);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiation_messages',
        },
        (payload) => {
          // Only refetch if the message is for one of this user's negotiations
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            fetchCounts();
          }, 500);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiation_messages',
        },
        () => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            fetchCounts();
          }, 500);
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (channelRef.current) {
        logger.log('[NegotiationContext] Cleaning up realtime subscription');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, fetchCounts]);

  const value = {
    ...state,
    activeLimit,
    canStartNew: state.activeCount < activeLimit,
    refetch: fetchCounts,
  };

  return (
    <NegotiationContext.Provider value={value}>{children}</NegotiationContext.Provider>
  );
};

export const useNegotiation = () => {
  const context = useContext(NegotiationContext);
  if (!context) {
    throw new Error('useNegotiation must be used within NegotiationProvider');
  }
  return context;
};

export default NegotiationContext;
